import React, { useState, useEffect } from 'react';
import { adminStatsService } from '../services/adminStatsService';
import { useSeasonContext } from '../context/SeasonContext';
import '../styles/PlayerComparison.css';

const PlayerComparison = ({ player1Name, player2Name }) => {
  const [stats, setStats] = useState({ player1: null, player2: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState('PASSING');
  const { selectedSeason } = useSeasonContext();

  const statCategories = ['PASSING', 'RUSHING', 'RECEIVING', 'DEFENSE', 'BLOCKING', 'KICKING'];

  useEffect(() => {
    if ((player1Name || player2Name) && selectedSeason) {
      fetchComparisonStats();
    }
  }, [player1Name, player2Name, selectedSeason]);

  const fetchComparisonStats = async () => {
    try {
      setLoading(true);
      const player1Stats = {};
      const player2Stats = {};

      for (const category of statCategories) {
        const result = await adminStatsService.getAllSeasonStats(category, selectedSeason.id);
        const p1 = result.find(p => p.NAME === player1Name);
        const p2 = result.find(p => p.NAME === player2Name);
        
        if (p1) player1Stats[category] = p1;
        if (p2) player2Stats[category] = p2;
      }

      setStats({
        player1: player1Stats,
        player2: player2Stats
      });
    } catch (err) {
      setError('Failed to load comparison stats');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="player-comparison-loading">Loading comparison stats...</div>;
  }

  if (error) {
    return <div className="player-comparison-error">{error}</div>;
  }

  const p1Data = stats.player1?.[activeCategory];
  const p2Data = stats.player2?.[activeCategory];

  if (!p1Data && !p2Data) {
    return <div className="player-comparison-empty">No stats available for selected category</div>;
  }

  const getStatKeys = () => {
    if (p1Data) return Object.keys(p1Data).filter(k => k !== 'NAME' && k !== 'TEAM' && k !== 'ID');
    if (p2Data) return Object.keys(p2Data).filter(k => k !== 'NAME' && k !== 'TEAM' && k !== 'ID');
    return [];
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'number') return value.toFixed(2);
    return value;
  };

  const compareValues = (v1, v2) => {
    if (!v1 || !v2) return 0;
    const n1 = parseFloat(v1);
    const n2 = parseFloat(v2);
    if (n1 > n2) return 1;
    if (n1 < n2) return -1;
    return 0;
  };

  const statKeys = getStatKeys();

  return (
    <div className="player-comparison-container">
      <div className="comparison-header">
        <div className="comparison-player">
          <h3>{player1Name}</h3>
          <p className="player-team">{p1Data?.TEAM || 'N/A'}</p>
        </div>
        <div className="comparison-vs">VS</div>
        <div className="comparison-player">
          <h3>{player2Name}</h3>
          <p className="player-team">{p2Data?.TEAM || 'N/A'}</p>
        </div>
      </div>

      <div className="comparison-category-tabs">
        {statCategories.map(category => (
          <button
            key={category}
            className={`category-tab ${activeCategory === category ? 'active' : ''}`}
            onClick={() => setActiveCategory(category)}
          >
            {category}
          </button>
        ))}
      </div>

      <div className="comparison-stats-table">
        <table>
          <thead>
            <tr>
              <th className="stat-name">Statistic</th>
              <th className="stat-value">{player1Name}</th>
              <th className="stat-value">{player2Name}</th>
            </tr>
          </thead>
          <tbody>
            {statKeys.map((key) => {
              const v1 = p1Data?.[key];
              const v2 = p2Data?.[key];
              const comparison = compareValues(v1, v2);
              
              return (
                <tr key={key}>
                  <td className="stat-name">{key.replace(/_/g, ' ')}</td>
                  <td className={`stat-value ${comparison > 0 ? 'winner' : ''}`}>
                    {formatValue(v1)}
                  </td>
                  <td className={`stat-value ${comparison < 0 ? 'winner' : ''}`}>
                    {formatValue(v2)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PlayerComparison;
